<template>
  <div class="personal-details" :class="{ 'personal-details--modal': isInModalMode }">
    <b-loading :active="isLoading" :is-full-page="false"></b-loading>

    <OmxPageHeader
        :title="headerTitle"
        :subtitle="headerSubtitle"
        :pills="headerPills"
        :sticky="!isInModalMode"
    >
      <template #actions>
        <template v-if="isEditMode">
          <b-button @click="cancelClicked" icon-left="close-thick" tabindex="31">{{ $t('common.cancel') }}</b-button>
          <OmxPermissionButton :allowed="canSave" type="is-primary" @click="saveClicked" icon-left="content-save" tabindex="32">{{ $t('common.save') }}</OmxPermissionButton>
        </template>
        <template v-else>
          <b-button v-if="!isInModalMode" @click="backClicked" icon-left="arrow-left">{{ $t('common.back') }}</b-button>
          <OmxPermissionButton :allowed="canEdit" type="is-primary" @click="editClicked" icon-left="pencil">{{ $t('common.edit') }}</OmxPermissionButton>
          <OmxPermissionButton :allowed="canDelete" type="is-danger" @click="deleteClicked" icon-left="delete">{{ $t('common.delete') }}</OmxPermissionButton>
        </template>
      </template>
    </OmxPageHeader>

    <OmxCard class="personal-details__card">
              <div class="columns">
                <div class="column">
                  <div class="columns">
                    <div class="column"></div>
                    <div class="column is-narrow">
                      <b-field>
                        <b-button v-if="isEditMode && isInNewMode" icon-left="account-hard-hat" class="button is-light" @click="switchUserInput" tabindex="9">
                          {{ userInputModeButtonTitle }}
                        </b-button>
                      </b-field>
                    </div>
                  </div>
                  <OmxTagInput
                      v-if="isEditMode && isSelectUserListMode"
                      v-model="selectedUsers"
                      :label="$t('personal-details-view.select-user-field')"
                      :data="filteredUsers"
                      :loading="isFetchingUsers"
                      :maxtags="1"
                      :allow-new="false"
                      :confirm-keys="[]"
                      field="fullName"
                      :placeholder="$t('personal-details-view.select-user-placeholder')"
                      tabindex="10"
                      @typing="getFilteredUsers"
                      @input="onUsersInput"
                      @blur="onUserInputBlur"
                      @add="onUserSelected"
                      @remove="onUserRemoved"
                  />
                  <OmxField
                      v-if="isEditMode && !isSelectUserListMode"
                      v-model="personalData.name.first"
                      :label="$t('personal-details-view.personal-firstname-field')"
                      :placeholder="$t('personal-details-view.personal-firstname-placeholder')"
                      :readonly="isUserNameReadOnly"
                      rules="required"
                      required
                      tabindex="11"
                  />
                  <OmxField
                      v-if="!isEditMode"
                      :label="$t('personal-details-view.personal-firstname-field')"
                      :value="personalData.name.first"
                      view-only
                  />
                  <OmxField
                      v-if="isEditMode && !isSelectUserListMode"
                      v-model="personalData.name.last"
                      :label="$t('personal-details-view.personal-lastname-field')"
                      :placeholder="$t('personal-details-view.personal-lastname-placeholder')"
                      :readonly="isUserNameReadOnly"
                      rules="required"
                      required
                      tabindex="12"
                  />
                  <OmxField
                      v-if="!isEditMode"
                      :label="$t('personal-details-view.personal-lastname-field')"
                      :value="personalData.name.last"
                      view-only
                  />
                  <OmxRadioGroup
                      v-model="personalData.group"
                      :label="$t('personal-details-view.personal-group-field')"
                      :options="personalGroupRadioOptions"
                      :view-only="!isEditMode"
                  />
                  <OmxTagInput
                      v-model="selectedAgencies"
                      :label="$t('personal-details-view.personal-agency-field')"
                      :data="filteredAgencies"
                      :loading="isFetchingCategories"
                      :view-only="!isEditMode"
                      :maxtags="1"
                      :allow-new="true"
                      :placeholder="$t('personal-details-view.personal-agency-placeholder')"
                      tabindex="14"
                      @typing="getFilteredAgencies"
                      @input="onAgenciesInput"
                      @blur="onAgencyInputBlur"
                  />
                  <OmxTagInput
                      v-model="selectedCategories"
                      :label="$t('personal-details-view.personal-category-field')"
                      :data="filteredCategories"
                      :loading="isFetchingCategories"
                      :view-only="!isEditMode"
                      :maxtags="1"
                      :allow-new="false"
                      :confirm-keys="[]"
                      :placeholder="$t('personal-details-view.personal-category-placeholder')"
                      tabindex="15"
                      @typing="getFilteredCategories"
                      @input="onCategoriesInput"
                      @blur="onCategoryInputBlur"
                  />
                  <OmxField
                      v-model="personalData.text"
                      type="textarea"
                      :label="$t('personal-details-view.personal-text-field')"
                      :placeholder="$t('personal-details-view.personal-text-placeholder')"
                      :view-only="!isEditMode"
                      tabindex="16"
                  />
                </div>
              </div>
    </OmxCard>
  </div>
</template>

<script>
import firebase from "firebase/app";
import OmxPageHeader from "@/components/OmxPageHeader.vue";
import OmxCard from "@/components/OmxCard.vue";
import OmxField from "@/components/OmxField.vue";
import OmxTagInput from "@/components/OmxTagInput.vue";
import OmxRadioGroup from "@/components/OmxRadioGroup.vue";

export default {
  name: "PersonalDetails",
  components: {
    OmxPageHeader,
    OmxCard,
    OmxField,
    OmxTagInput,
    OmxRadioGroup
  },
  props: {
    'project_id': {
      type: String,
    },
    'personal_id': {
      type: String,
    },
    'is_template': {
      type: Boolean,
    }
  },
  data() {
    return {
      personalData: {
        name: { first: "", last: "" }
      },
      isLoading: false,
      isEditMode: false,
      isSelectUserListMode: false,
      isUserNameReadOnly: false,
      isFetchingCategories: false,
      isFetchingUsers: false,
      categoricalData: {},
      filteredCategories: [],
      selectedCategories: [],
      filteredAgencies: [],
      filteredUsers: [],
      selectedAgencies: [],
      selectedUsers: [],
      usersData: [],
      userInputModeButtonTitle: this.$t('personal-details-view.select-user-placeholder')
    }
  },
  methods: {
    readPersonalData: function () {
      this.isLoading = true;
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const getPersonalFunction = functions.httpsCallable(this.getSubmitEndpoint());
      getPersonalFunction({
        action: "get_personal_by_id",
        project_id: this.project_id,
        document_id: this.personal_id,
        fill_meta: false
      }).then((result) => {
        this.isLoading = false;
        this.personalData = result.data["personnel"];
        const agency = this.personalData.agency
        if (agency !== undefined && agency !== "") {
          this.selectedAgencies[0] = agency
        }
        const category = this.personalData.category
        if (category !== undefined && category !== "") {
          this.selectedCategories[0] = category
        }
      }).catch((error) => {
        this.isLoading = false;
        this.showError(error.message)
        console.log("Error getting personnel:", error?.code);
      });
    },
    createPersonal: function (updateData) {
      this.isLoading = true
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const insertPersonalFunction = functions.httpsCallable(this.getSubmitEndpoint());
      updateData.action = "insert_personal";
      insertPersonalFunction(updateData).then(() => {
        this.isLoading = false
        this.showSuccess(this.$t('messages.personal-created'));
        this.$emit('personalAdded');
      }).catch((error) => {
        this.isLoading = false
        this.showError(this.$t('messages.save-error'))
        console.error("Error creating personnel:", error?.code);
      });
    },
    updatePersonal: function (updateData) {
      this.isLoading = true;
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const updatePersonalFunction = functions.httpsCallable(this.getSubmitEndpoint());
      updateData.action = "update_personal";
      updatePersonalFunction(updateData).then(() => {
        this.isLoading = false;
        this.isEditMode = false;
        this.showSuccess(this.$t('messages.personal-updated'));
      }).catch((error) => {
        this.isLoading = false;
        this.showError(this.$t('messages.save-error'))
        console.error("Error updating personnel:", error?.code);
      });
    },
    deletePersonal: function () {
      this.isLoading = true;
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const deletePersonalFunction = functions.httpsCallable(this.getSubmitEndpoint());
      deletePersonalFunction({
        action: "delete_personal",
        project_id: this.project_id,
        document_id: this.personal_id,
      }).then(() => {
        this.isLoading = false;
        this.isEditMode = false;
        this.showSuccess(this.$t('messages.personal-deleted'));
        this.$emit('personalDeleted');
        this.$router.back();
      }).catch((error) => {
        this.showError(error.message)
        this.isLoading = false;
        console.log("Error getting personal:", error?.code);
      });
    },
    readCategoricalData: function () {
      this.isFetchingCategories = true
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const getCategoriesFunction = functions.httpsCallable(this.getSubmitEndpoint());
      getCategoriesFunction({
        action: "get_categories",
        collections: ["personnel"]
      }).then((result) => {
        this.isFetchingCategories = false
        this.categoricalData = result.data["personnel"]
        this.filteredCategories = this.categoricalData.category
        this.filteredAgencies = this.categoricalData.agency
      }).catch((error) => {
        this.isFetchingCategories = false
        this.showError(error.message)
        console.log("Error getting categories:", error?.code);
      });
    },
    getUsers: function () {
      this.isFetchingUsers = true
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const getUsersFunction = functions.httpsCallable(this.getSubmitEndpoint());
      const payload = {
        action: "get_users", fill_meta: true,
        filters: [['is_archived', '==', false]]
      }
      payload.filters.push(['roles', 'array_contains_any', ['admin', 'project_manager', 'site_manager']])
      getUsersFunction(payload)
          .then((result) => {
            result.data["users"].forEach((user) => {
              this.usersData.push({
                user: user,
                fullName: this.getFullName(user.name),
                firstName: user.name.first,
                lastName: user.name.last,
                role: user.roles[0]
              });
            });
            this.filteredUsers = this.usersData
            this.isFetchingUsers = false
          })
          .catch((error) => {
            console.log("Error getting users:", error?.code);
            this.showError(error.message)
            this.isFetchingUsers = false
          });
    },
    /*
     UI Events
     */
    editClicked: function () {
      this.isEditMode = !this.isEditMode;
    },
    async deleteClicked() {
      const ok = await this.$omxConfirm({
        title: this.$t('personal-details-view.delete-personal-confirm-title').toString(),
        message: this.$t('personal-details-view.delete-personal-confirm-message').toString(),
        confirmText: this.$t('personal-details-view.delete-personal-confirm-button').toString(),
        cancelText: this.$t('common.cancel').toString(),
        type: 'is-danger',
        icon: 'delete'
      });
      if (ok) this.deletePersonal();
    },
    cancelClicked: function () {
      if (this.isInNewMode) {
        this.$emit('close');
      } else if (this.isEditMode) {
        this.readPersonalData();
        this.isEditMode = false;
      }
    },
    backClicked: function () {
      this.$router.back();
    },
    saveClicked: function () {
      // Required fields must be present before we attempt to save.
      const name = this.personalData.name || {};
      const firstName = (name.first || "").toString().trim();
      const lastName = (name.last || "").toString().trim();
      if (!firstName || !lastName) {
        this.showError(this.$t('messages.required-fields-error'));
        return;
      }

      const dataFieldsToUpdate = {
        project_id: this.project_id,
        name: this.personalData.name,
        group: this.personalData.group,
        text: this.personalData.text
      }
      if (this.selectedCategories[0] !== undefined) {
        dataFieldsToUpdate.category = this.selectedCategories[0]
      }
      if (this.selectedAgencies[0] !== undefined) {
        dataFieldsToUpdate.agency = this.selectedAgencies[0]
      }
      if (this.isEmptyOrNull(dataFieldsToUpdate.name) ||
          this.isEmptyOrNull(dataFieldsToUpdate.group) ||
          this.isEmptyOrNull(dataFieldsToUpdate.category) ||
          this.isEmptyOrNull(dataFieldsToUpdate.agency)) {
        this.showError(this.$t('messages.empty-form-error').toString())
        return
      }
      if (this.isInNewMode) {
        this.createPersonal(dataFieldsToUpdate);
      } else {
        dataFieldsToUpdate.document_id = this.personal_id;
        this.updatePersonal(dataFieldsToUpdate);
      }
    },
    onCategoriesInput() {
      this.filteredCategories = this.categoricalData.category
    },
    getFilteredCategories(text) {
      this.filteredCategories = this.categoricalData.category.filter((option) => {
        return option
            .toString()
            .toLowerCase()
            .indexOf(text.toLowerCase()) >= 0
      })
    },
    onAgenciesInput() {
      this.filteredAgencies = this.categoricalData.agency
    },
    getFilteredAgencies(text) {
      this.filteredAgencies = this.categoricalData.agency.filter((option) => {
        return option
            .toString()
            .toLowerCase()
            .indexOf(text.toLowerCase()) >= 0
      })
    },
    onUsersInput() {
      this.filteredUsers = this.usersData
    },
    getFilteredUsers(text) {
      this.filteredUsers = this.usersData.filter((option) => {
        return option.fullName.toLowerCase().indexOf(text.toLowerCase()) >= 0
      })
    },
    onCategoryInputBlur() {
      this.$refs.categoryInput.newTag = '';
    },
    onAgencyInputBlur() {
      this.$refs.agencyInput.addTag()
    },
    onUserInputBlur() {
      this.$refs.userInput.newTag = '';
    },
    onUserSelected(user) {
      this.personalData.name.first = user.firstName
      this.personalData.name.last = user.lastName
      this.isUserNameReadOnly = true
    },
    onUserRemoved() {
      this.personalData.name.first = ''
      this.personalData.name.last = ''
      this.isUserNameReadOnly = false
    },
    switchUserInput() {
      if (this.isSelectUserListMode) {
        this.$refs.userInput.removeLastTag()
        this.userInputModeButtonTitle = this.$t('personal-details-view.select-user-from-list');
      } else {
        this.personalData.name.first = ''
        this.personalData.name.last = ''
        this.userInputModeButtonTitle = this.$t('personal-details-view.select-custom-user');
      }
      this.isSelectUserListMode = !this.isSelectUserListMode
    }
  },
  mounted() {
    if ((!this.is_template && !this.currentUserAccess("PersonalDetails", this.UserActions.VIEW, this.project_id))
        || (this.is_template && !this.currentUserAccess("ProjectTemplates", this.UserActions.VIEW))) {
      console.log("Page access denied.");
      this.$router.replace("/projects/").catch(() => {
      });
    }
    this.getUsers()
    this.readCategoricalData()
    if (this.isInNewMode) {
      console.log("creating new personal");
      this.isEditMode = true;
    } else {
      this.isEditMode = false;
      this.readPersonalData();
    }
  },
  computed: {
    isInNewMode() {
      return this.personal_id === "new";
    },
    isInModalMode() {
      return this.$route.name !== 'PersonalDetails';
    },
    pageTitle() {
      if (this.isInNewMode) {
        return this.$t('personal-details-view.new-personal-title')
      } else if (this.isEditMode) {
        return this.$t('personal-details-view.edit-personal-title') + " " + this.getAdjustedTitle(this.getFullName(this.personalData.name))
      } else {
        return this.$t('personal-details-view.view-personal-title') + " " + this.getAdjustedTitle(this.getFullName(this.personalData.name))
      }
    },
    personalGroup() {
      if (!this.isEditMode) {
        const st = this.personalGroupOptions.find(obj => {
          return obj.id === this.personalData.group
        });
        if (st !== undefined)
          return this.$t(st.translationKey);
      }
      return "";
    },
    personalGroupRadioOptions() {
      return (this.personalGroupOptions || []).map(opt => ({
        value: opt.id,
        label: this.$t(opt.translationKey)
      }));
    },
    headerTitle() {
      if (this.isInNewMode) return this.$t('personal-details-view.new-personal-title');
      return this.getAdjustedTitle(this.getFullName(this.personalData.name));
    },
    headerSubtitle() {
      const parts = [];
      if (this.personalData.agency) parts.push(this.personalData.agency);
      if (this.personalData.category) parts.push(this.personalData.category);
      return parts.join(' · ') || null;
    },
    headerPills() {
      const pills = [];
      if (this.personalData.group) {
        const tone = this.personalData.group === 'internal' ? 'draft' : 'warn';
        pills.push({ label: this.personalGroup, tone });
      }
      return pills;
    },
    canEdit() {
      return !this.isEditMode
          && ((!this.is_template && this.currentUserAccess('PersonalDetails', 'UPDATE', this.project_id))
              || (this.is_template && this.currentUserAccess('ProjectTemplates', 'UPDATE')));
    },
    canSave() {
      return (!this.is_template && this.currentUserAccess('PersonalDetails', 'UPDATE', this.project_id))
          || (this.is_template && this.currentUserAccess('ProjectTemplates', 'UPDATE'));
    },
    canDelete() {
      return !this.isEditMode && !this.isInNewMode
          && ((!this.is_template && this.currentUserAccess('PersonalDetails', 'DELETE', this.project_id))
              || (this.is_template && this.currentUserAccess('ProjectTemplates', 'DELETE')));
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/tokens";

.personal-details {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.personal-details--modal { padding: 4px; }
.personal-details__card ::v-deep .columns:last-child { margin-bottom: 0; }
</style>
